<template>
    <Component :is="isLink ? 'a' : 'div'"
               :href="url"
               :target="url_target || '_self'"
               class="nav-item"
               @click="handleClick">
        <div class="nav-item__content">
            <div class="nav-item__content--left">
                <NavigationItemIcon v-if="icon && menuStore.isDesktop"
                                    :icon="icon" />
                <div class="nav-item__title">
                    {{ title }}
                </div>
            </div>
            <NavigationBadge v-if="badge"
                             :badgeType="badge" />
        </div>
        <Arrow v-if="showArrow"
               class="nav-item__arrow"
               :direction="arrowDirection"
               :variant="menuStore.isDesktop ? ArrowVariantEnum.THIN : ArrowVariantEnum.NORMAL" />
    </Component>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import Arrow from '../../../../components/icons/Arrow.vue';
    import { DirectionsEnum } from '@/js/enums/directions';
    import { useMenuStore } from '@/js/store/menu/MenuStore';
    import { ArrowVariantEnum } from '@/js/types/arrowVariants';
    import NavigationBadge from './NavigationBadge.vue';
    import { INavigationItem } from '../../types/menuTypes';
    import NavigationItemIcon from './NavigationItemIcon.vue';
    import { useSubscribeModalStore } from '@/js/store/subscribe/SubscribeModalStore';
    import { NavigationActionsEnum } from '../../enums/menuEnums';

    interface IProps extends INavigationItem {
        showArrow: boolean,
        arrowDirection: DirectionsEnum,
        hasChildren: boolean,
    }

    const props = defineProps<IProps>();

    const menuStore = useMenuStore();
    const subscribeModalStore = useSubscribeModalStore();

    const hasUrl = !!props.url;
    const hasAction = !!props.action;
    const shouldBeLink = menuStore.isDesktop || !props.hasChildren;

    const isLink = computed(() => hasUrl && !hasAction && shouldBeLink);

    const handleClick = () => {
        if (!isLink.value && hasAction) {
            subscribeModalStore.setSubscribeModal(true, props.action === NavigationActionsEnum.NEWSLETTER_MODAL);
        }
    };
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/partials/mixins" as mixins;

.nav-item {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    text-decoration: none;

    &__content {
        display: flex;
        overflow: hidden;
        flex: 1;
        align-items: center;
        justify-content: space-between;

        &--left {
            display: flex;
            overflow: hidden;
            align-items: center;
            gap: 6px;
        }
    }

    &__title {
        @include mixins.custom-line-clamp(2);
        line-height: 18px;
    }

    &__arrow {
        :deep(svg) {
            width: 20px;
            height: 20px;
        }
    }
}

.mobile-nav {
    .list-item {
        .nav-item {
            height: legacyVariables.$menu-item-height;
            align-items: center;
        }
    }

    .nav-item {
        font-size: 16px;
        font-weight: 500;

        &__arrow {
            :deep(svg) {
                width: 12px;
                height: 12px;
                margin-left: 8px;
            }
        }
    }

}

.dark-theme {
    .nav-item {
        fill: legacyVariables.$white;

        &__arrow {
            :deep(svg) {
                stroke: legacyVariables.$white;
            }
        }
    }
}
</style>
